import React, { memo, useEffect, useRef, useState } from 'react'
import Modal from 'react-modal'
import { get } from 'lodash'
import { customFuncsApi } from '../../api'
import ClipLoader from 'react-spinners/ClipLoader'
import html2PDF from 'jspdf-html2canvas/index'
import { useTranslation } from 'react-i18next'

const customStyles = {
	content: {
		top: '50%',
		left: '50%',
		right: 'auto',
		width: '90%',
		maxWidth: 900,
		height: '90%',
		bottom: 'auto',
		marginRight: '-50%',
		transform: 'translate(-50%, -50%)',
		border: 'none',
		borderRadius: 15,
		overflowX: 'auto',
		padding: 0,
	},
}

const PRIMARY_COLOR = '#FFC801'
const PRIMARY_SOFT = '#FFF8D6'
const PRIMARY_BORDER = '#FDE68A'
const PRIMARY_TEXT = '#8A6A00'

const ClientPortfolioModal = ({ getRef = () => {} }) => {
	const { t } = useTranslation()

	const [isOpenModal, setIsOpenModal] = useState(false)
	const [isLoading, setIsLoading] = useState(false)
	const [cardCode, setCardCode] = useState('')
	const [cardName, setCardName] = useState('')
	const [summary, setSummary] = useState(null)
	const [timeliness, setTimeliness] = useState(null)
	const [avgMonthlyPayment, setAvgMonthlyPayment] = useState(null)
	const [firstPaymentStats, setFirstPaymentStats] = useState(null)
	const [unpaidSummary, setUnpaidSummary] = useState(null)

	const modalContentRef = useRef()

	useEffect(() => {
		const ref = {
			open: (code) => {
				setIsOpenModal(true)
				setCardCode(code)
				fetchData(code)
			},
			close: () => setIsOpenModal(false),
		}
		getRef(ref)
	}, [])

	const getInstallmentsPath = (fn, code) =>
		`installments/${fn}?CardCode=${encodeURIComponent(code)}`

	const fetchData = async (code) => {
		setIsLoading(true)
		setCardName('')
		setSummary(null)
		setTimeliness(null)
		setAvgMonthlyPayment(null)
		setFirstPaymentStats(null)
		setUnpaidSummary(null)
		try {
			const [
				summaryRes,
				timelinessRes,
				avgMonthlyPaymentRes,
				firstPaymentsRes,
				unpaidRes,
			] =
				await Promise.allSettled([
					customFuncsApi.get(getInstallmentsPath('getCardSummary', code)),
					customFuncsApi.get(getInstallmentsPath('getCardPaymentTimeliness', code)),
					customFuncsApi.get(getInstallmentsPath('getCardAvgMonthlyPayment', code)),
					customFuncsApi.get(getInstallmentsPath('getCardFirstPayments', code)),
					customFuncsApi.get(getInstallmentsPath('getCardUnpaidInstallments', code)),
				])

			if (summaryRes.status === 'fulfilled') {
				const summaryData = get(summaryRes.value, 'data.value[0]', null)
				setSummary(summaryData)
				if (summaryData) setCardName(get(summaryData, 'CardName', ''))
			} else {
				console.error(
					'ClientPortfolioModal summary fetch error',
					summaryRes.reason,
				)
			}

			if (timelinessRes.status === 'fulfilled') {
				setTimeliness(get(timelinessRes.value, 'data.value[0]', null))
			} else {
				console.error(
					'ClientPortfolioModal timeliness fetch error',
					timelinessRes.reason,
				)
			}

			if (avgMonthlyPaymentRes.status === 'fulfilled') {
				setAvgMonthlyPayment(
					get(avgMonthlyPaymentRes.value, 'data.value[0]', null),
				)
			} else {
				console.error(
					'ClientPortfolioModal avg monthly payment fetch error',
					avgMonthlyPaymentRes.reason,
				)
			}

			if (firstPaymentsRes.status === 'fulfilled') {
				setFirstPaymentStats(get(firstPaymentsRes.value, 'data.value[0]', null))
			} else {
				console.error(
					'ClientPortfolioModal first payments fetch error',
					firstPaymentsRes.reason,
				)
			}

			if (unpaidRes.status === 'fulfilled') {
				setUnpaidSummary(get(unpaidRes.value, 'data.value[0]', null))
			} else {
				console.error(
					'ClientPortfolioModal unpaid installments fetch error',
					unpaidRes.reason,
				)
			}
		} catch (err) {
			console.error('ClientPortfolioModal fetchData error', err)
		} finally {
			setIsLoading(false)
		}
	}

	const handleDownload = () => {
		if (modalContentRef.current) {
			html2PDF(modalContentRef.current, {
				jsPDF: { unit: 'px', format: 'a4' },
				html2canvas: { scale: 2 },
				imageType: 'image/jpeg',
				output: `portfolio_${cardCode}.pdf`,
			})
		}
	}

	// Values from API come as strings ("1121") — parse before formatting
	const num = (val) => (val != null ? Number(val) : null)

	const fmt = (val) => {
		const n = num(val)
		if (n == null || isNaN(n)) return '–'
		return n.toLocaleString('en-US', {
			minimumFractionDigits: 2,
			maximumFractionDigits: 2,
		})
	}

	const fmtCount = (val) => {
		const n = num(val)
		if (n == null || isNaN(n) || n === 0) return null
		return n
	}

	// Compute Normal = Total - Closed (API doesn't return it directly)
	const totalCount = num(get(summary, 'TotalSalesCount'))
	const closedCount = num(get(summary, 'ClosedSalesCount'))
	const normalCount =
		totalCount != null && closedCount != null ? totalCount - closedCount : null

	const totalSum = num(get(summary, 'TotalSalesSum'))
	const closedSum = num(get(summary, 'ClosedSalesSum'))
	const normalSum =
		totalSum != null && closedSum != null ? totalSum - closedSum : null

	const salesCards = [
		{ label: t('totalSales'), count: totalCount, sum: totalSum },
		{ label: t('openSales'), count: normalCount, sum: normalSum },
		{ label: t('closedSales'), count: closedCount, sum: closedSum },
	]

	const timelinessRows = [
		{
			label: t('onTime'),
			countKey: 'Vaqtida_Count',
			sumKey: 'Vaqtida_Sum',
			color: PRIMARY_TEXT,
		},
		{
			label: '1–15 ' + t('days'),
			countKey: 'Days_1_15_Count',
			sumKey: 'Days_1_15_Sum',
		},
		{
			label: '16–30 ' + t('days'),
			countKey: 'Days_16_30_Count',
			sumKey: 'Days_16_30_Sum',
		},
		{
			label: '31–60 ' + t('days'),
			countKey: 'Days_31_60_Count',
			sumKey: 'Days_31_60_Sum',
		},
		{
			label: '61–90 ' + t('days'),
			countKey: 'Days_61_90_Count',
			sumKey: 'Days_61_90_Sum',
		},
		{
			label: '90+ ' + t('days'),
			countKey: 'Days_90_Plus_Count',
			sumKey: 'Days_90_Plus_Sum',
		},
	]
	const unpaidRows = [
		{
			label: t('dueToday'),
			countKey: 'Today_Count',
			sumKey: 'Today_Sum',
			color: PRIMARY_TEXT,
		},
		{
			label: '1–15 ' + t('days'),
			countKey: 'Days_1_15_Count',
			sumKey: 'Days_1_15_Sum',
		},
		{
			label: '16–30 ' + t('days'),
			countKey: 'Days_16_30_Count',
			sumKey: 'Days_16_30_Sum',
		},
		{
			label: '31–60 ' + t('days'),
			countKey: 'Days_31_60_Count',
			sumKey: 'Days_31_60_Sum',
		},
		{
			label: '61–90 ' + t('days'),
			countKey: 'Days_61_90_Count',
			sumKey: 'Days_61_90_Sum',
		},
		{
			label: '90+ ' + t('days'),
			countKey: 'Days_90_Plus_Count',
			sumKey: 'Days_90_Plus_Sum',
			color: PRIMARY_TEXT,
		},
	]
	const avgPaymentCards = [
		{
			label: t('avgInstallmentPayment3Months'),
			value: get(avgMonthlyPayment, 'Avg3MonthsPayment'),
		},
		{
			label: t('avgInstallmentPayment6Months'),
			value: get(avgMonthlyPayment, 'Avg6MonthsPayment'),
		},
	]

	return (
		<Modal
			isOpen={isOpenModal}
			onRequestClose={() => setIsOpenModal(false)}
			style={customStyles}
			contentLabel="Client Portfolio Overview"
			ariaHideApp={false}
		>
			<div
				ref={modalContentRef}
				style={{ padding: '28px 32px', minWidth: 0, boxSizing: 'border-box' }}
			>
				{/* Header */}
				<div
					style={{
						display: 'flex',
						justifyContent: 'space-between',
						alignItems: 'center',
						marginBottom: 4,
					}}
				>
					<span style={{ fontSize: 13, color: '#6b7280', fontWeight: 500 }}>
						{t('clientPortfolioOverview')}
					</span>
					<button
						onClick={() => setIsOpenModal(false)}
						style={{
							background: 'none',
							border: 'none',
							cursor: 'pointer',
							fontSize: 22,
							color: '#6b7280',
							lineHeight: 1,
							padding: '0 4px',
						}}
					>
						×
					</button>
				</div>
				<h2
					style={{
						fontSize: 22,
						fontWeight: 700,
						marginBottom: 24,
						marginTop: 0,
					}}
				>
					{cardCode}
					{cardName ? ` · ${cardName}` : ''}
				</h2>

				{isLoading ? (
					<div
						style={{ display: 'flex', justifyContent: 'center', padding: 80 }}
					>
						<ClipLoader loading={true} size={50} />
					</div>
				) : (
					<>
						{/* SALES PERFORMANCE */}
						<div style={{ marginBottom: 28 }}>
							<p
								style={{
									fontSize: 11,
									fontWeight: 700,
									letterSpacing: 1,
									color: '#6b7280',
									marginBottom: 12,
									textTransform: 'uppercase',
								}}
							>
								{t('salesPerformance')}
							</p>
							<div
								style={{
									display: 'grid',
									gridTemplateColumns: 'repeat(3, 1fr)',
									gap: 14,
								}}
							>
								{salesCards.map(({ label, count, sum }) => (
									<div
										key={label}
										style={{
											background: '#f9fafb',
											borderRadius: 12,
											padding: '18px 16px',
											textAlign: 'center',
											border: '1px solid #f3f4f6',
										}}
									>
										<p
											style={{
												fontSize: 12,
												color: '#6b7280',
												marginBottom: 8,
												margin: '0 0 8px',
											}}
										>
											{label}
										</p>
										<p
											style={{
												fontSize: 28,
												fontWeight: 800,
												margin: 0,
												lineHeight: 1.1,
											}}
										>
											{count != null ? count : '–'}
										</p>
										<p
											style={{
												fontSize: 12,
												color: '#6b7280',
												fontWeight: 700,
												margin: '6px 0 0',
											}}
										>
											{sum != null ? `${fmt(sum)} USD` : ''}
										</p>
									</div>
								))}
							</div>
						</div>

						{/* Two-column layout on desktop */}
						<div
							style={{
								display: 'grid',
								gridTemplateColumns: 'minmax(0,1fr) minmax(0,1fr)',
								gap: 24,
								marginBottom: 28,
							}}
						>
							{/* INSTALLMENTS BREAKDOWN */}
							<div>
								<div
									style={{
										display: 'flex',
										justifyContent: 'space-between',
										alignItems: 'center',
										marginBottom: 10,
									}}
								>
									<p
										style={{
											fontSize: 11,
											fontWeight: 700,
											letterSpacing: 1,
											color: '#6b7280',
											margin: 0,
											textTransform: 'uppercase',
										}}
									>
										{t('installmentsBreakdown')}
									</p>
									<span
										style={{
											display: 'flex',
											justifyContent: 'flex-end',
											gap: 16,
											minWidth: 220,
										}}
									>
										<span
											style={{
												fontSize: 10,
												color: '#9ca3af',
												textTransform: 'uppercase',
												letterSpacing: 0.6,
											}}
										>
											{t('quantity')}
										</span>
										<span
											style={{
												fontSize: 10,
												color: '#9ca3af',
												textTransform: 'uppercase',
												letterSpacing: 0.6,
												minWidth: 110,
												textAlign: 'right',
											}}
										>
											{t('total')}
										</span>
									</span>
								</div>
								<div
									style={{
										borderRadius: 10,
										overflow: 'hidden',
										border: '1px solid #f3f4f6',
									}}
								>
									{timelinessRows.map(
										({ label, countKey, sumKey, color }, idx) => {
											const count = fmtCount(get(timeliness, countKey))
											const sum = num(get(timeliness, sumKey))
											return (
												<div
													key={countKey}
													style={{
														display: 'flex',
														justifyContent: 'space-between',
														alignItems: 'center',
														padding: '10px 14px',
														background: idx % 2 === 0 ? '#fff' : '#f9fafb',
													}}
												>
													<span
														style={{ fontSize: 13, color: color || '#111827' }}
													>
														{label}
													</span>
													<span
														style={{
															display: 'flex',
															justifyContent: 'flex-end',
															alignItems: 'center',
															gap: 16,
															minWidth: 220,
														}}
													>
														<span
															style={{
																minWidth: 40,
																textAlign: 'right',
																fontSize: 13,
																fontWeight: 700,
																color: '#111827',
															}}
														>
															{count != null ? (
																count
															) : (
																<span style={{ color: '#9ca3af' }}>–</span>
															)}
														</span>
														<span
															style={{
																width: 1,
																height: 26,
																background: '#e5e7eb',
																flexShrink: 0,
															}}
														/>
														<span
															style={{
																minWidth: 110,
																textAlign: 'right',
																fontSize: 12,
																fontWeight: 700,
																color: 'black',
															}}
														>
															{sum != null ? (
																`${fmt(sum)} USD`
															) : (
																<span style={{ color: '#9ca3af' }}>–</span>
															)}
														</span>
													</span>
												</div>
											)
										},
									)}
								</div>

								{/* AVG PAYMENT */}
								<div style={{ marginTop: 16 }}>
									<p
										style={{
											fontSize: 11,
											fontWeight: 700,
											letterSpacing: 1,
											color: PRIMARY_TEXT,
											marginBottom: 8,
											textTransform: 'uppercase',
										}}
									>
										{t('avgInstallmentPayment')}
									</p>
									<div
										style={{
											display: 'grid',
											gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
											gap: 12,
										}}
									>
										{avgPaymentCards.map((item) => {
											const value = num(item.value)

											return (
												<div
													key={item.label}
													style={{
														background: PRIMARY_SOFT,
														border: `1px solid ${PRIMARY_BORDER}`,
														borderRadius: 10,
														padding: '16px 20px',
													}}
												>
													<p
														style={{
															fontSize: 12,
															color: PRIMARY_TEXT,
															margin: '0 0 8px',
														}}
													>
														{item.label}
													</p>
													<div
														style={{
															display: 'flex',
															alignItems: 'baseline',
															gap: 8,
														}}
													>
														<span
															style={{
																fontSize: 28,
																fontWeight: 800,
																lineHeight: 1,
																color: PRIMARY_TEXT,
															}}
														>
															{fmt(value)}
														</span>
														{value != null && !isNaN(value) ? (
															<span
																style={{
																	fontSize: 14,
																	color: PRIMARY_TEXT,
																	fontWeight: 600,
																}}
															>
																USD
															</span>
														) : null}
													</div>
												</div>
											)
										})}
									</div>
								</div>

								<div style={{ marginTop: 16 }} hidden>
									<p
										style={{
											fontSize: 11,
											fontWeight: 700,
											letterSpacing: 1,
											color: PRIMARY_TEXT,
											marginBottom: 8,
											textTransform: 'uppercase',
										}}
									>
										{t('firstPaymentStatistics')}
									</p>
									<div
										style={{
											display: 'grid',
											gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
											gap: 12,
										}}
									>
										<div
											style={{
												background: PRIMARY_SOFT,
												borderRadius: 10,
												padding: '16px 18px',
												border: `1px solid ${PRIMARY_BORDER}`,
											}}
										>
											<p
												style={{
													fontSize: 12,
													color: PRIMARY_TEXT,
													margin: '0 0 8px',
												}}
											>
												{t('totalFirstPayment')}
											</p>
											<div
												style={{
													display: 'flex',
													alignItems: 'baseline',
													gap: 8,
												}}
											>
												<span
													style={{
														fontSize: 26,
														fontWeight: 800,
														lineHeight: 1,
														color: PRIMARY_TEXT,
													}}
												>
													{fmt(get(firstPaymentStats, 'TotalFirstPayments'))}
												</span>
												<span
													style={{
														fontSize: 13,
														color: PRIMARY_TEXT,
														fontWeight: 600,
													}}
												>
													USD
												</span>
											</div>
										</div>
										<div
											style={{
												background: PRIMARY_SOFT,
												borderRadius: 10,
												padding: '16px 18px',
												border: `1px solid ${PRIMARY_BORDER}`,
											}}
										>
											<p
												style={{
													fontSize: 12,
													color: PRIMARY_TEXT,
													margin: '0 0 8px',
												}}
											>
												{t('avgFirstPayment')}
											</p>
											<div
												style={{
													display: 'flex',
													alignItems: 'baseline',
													gap: 8,
												}}
											>
												<span
													style={{
														fontSize: 26,
														fontWeight: 800,
														lineHeight: 1,
														color: PRIMARY_TEXT,
													}}
												>
													{fmt(get(firstPaymentStats, 'AvgFirstPayment'))}
												</span>
												<span
													style={{
														fontSize: 13,
														color: PRIMARY_TEXT,
														fontWeight: 600,
													}}
												>
													USD
												</span>
											</div>
										</div>
									</div>
								</div>
							</div>

							{/* UNPAID INSTALLMENTS */}
							<div>
								<div
									style={{
										display: 'flex',
										justifyContent: 'space-between',
										alignItems: 'center',
										marginBottom: 10,
									}}
								>
									<p
										style={{
											fontSize: 11,
											fontWeight: 700,
											letterSpacing: 1,
											color: PRIMARY_TEXT,
											margin: 0,
											textTransform: 'uppercase',
										}}
									>
										{t('unpaidInstallments')}
									</p>
									<span
										style={{
											display: 'flex',
											justifyContent: 'flex-end',
											gap: 16,
											minWidth: 220,
										}}
									>
										<span
											style={{
												fontSize: 10,
												color: '#9ca3af',
												textTransform: 'uppercase',
												letterSpacing: 0.6,
											}}
										>
											{t('quantity')}
										</span>
										<span
											style={{
												fontSize: 10,
												color: '#9ca3af',
												textTransform: 'uppercase',
												letterSpacing: 0.6,
												minWidth: 110,
												textAlign: 'right',
											}}
										>
											{t('total')}
										</span>
									</span>
								</div>
								{!unpaidSummary ? (
									<p
										style={{
											color: '#9ca3af',
											textAlign: 'center',
											padding: 20,
										}}
									>
										{t('noData')}
									</p>
								) : (
									<>
										<div
											style={{
												borderRadius: 10,
												overflow: 'hidden',
												border: '1px solid #f3f4f6',
											}}
										>
											{unpaidRows.map(
												({ label, countKey, sumKey, color }, idx) => {
													const count = fmtCount(get(unpaidSummary, countKey))
													const sum = num(get(unpaidSummary, sumKey))
													return (
														<div
															key={countKey}
															style={{
																display: 'flex',
																justifyContent: 'space-between',
																alignItems: 'center',
																padding: '10px 14px',
																background:
																	idx % 2 === 0 ? '#fff' : '#f9fafb',
															}}
														>
															<span
																style={{
																	fontSize: 13,
																	color: color || '#111827',
																}}
															>
																{label}
															</span>
															<span
																style={{
																	display: 'flex',
																	justifyContent: 'flex-end',
																	alignItems: 'center',
																	gap: 16,
																	minWidth: 220,
																}}
															>
																<span
																	style={{
																		minWidth: 40,
																		textAlign: 'right',
																		fontSize: 13,
																		fontWeight: 700,
																		color: '#111827',
																	}}
																>
																	{count != null ? (
																		count
																	) : (
																		<span style={{ color: '#9ca3af' }}>–</span>
																	)}
																</span>
																<span
																	style={{
																		width: 1,
																		height: 26,
																		background: '#e5e7eb',
																		flexShrink: 0,
																	}}
																/>
																<span
																	style={{
																		minWidth: 110,
																		textAlign: 'right',
																		fontSize: 12,
																		fontWeight: 700,
																		color: 'black',
																	}}
																>
																	{sum != null ? (
																		`${fmt(sum)} USD`
																	) : (
																		<span style={{ color: '#9ca3af' }}>–</span>
																	)}
																</span>
															</span>
														</div>
													)
												},
											)}
											<div
												style={{
													display: 'flex',
													justifyContent: 'space-between',
													alignItems: 'center',
													padding: '12px 14px',
													background: PRIMARY_SOFT,
													borderTop: `1px solid ${PRIMARY_BORDER}`,
												}}
											>
												<span
													style={{
														fontSize: 13,
														fontWeight: 700,
														color: PRIMARY_TEXT,
													}}
												>
													{t('total')}
												</span>
												<span
													style={{
														display: 'flex',
														justifyContent: 'flex-end',
														alignItems: 'center',
														gap: 16,
														minWidth: 220,
													}}
												>
													<span
														style={{
															minWidth: 40,
															textAlign: 'right',
															fontSize: 13,
															fontWeight: 700,
															color: PRIMARY_TEXT,
														}}
													>
														{fmtCount(get(unpaidSummary, 'TotalOverdueCount')) != null ? (
															fmtCount(get(unpaidSummary, 'TotalOverdueCount'))
														) : (
															<span style={{ color: '#9ca3af' }}>–</span>
														)}
													</span>
													<span
														style={{
															width: 1,
															height: 26,
															background: PRIMARY_BORDER,
															flexShrink: 0,
														}}
													/>
													<span
														style={{
															minWidth: 110,
															textAlign: 'right',
															fontSize: 12,
															fontWeight: 700,
															color: PRIMARY_TEXT,
														}}
													>
														{num(get(unpaidSummary, 'TotalOverdueSum')) != null ? (
															`${fmt(get(unpaidSummary, 'TotalOverdueSum'))} USD`
														) : (
															<span style={{ color: '#9ca3af' }}>–</span>
														)}
													</span>
												</span>
											</div>
										</div>

										<div style={{ marginTop: 16 }}>
											<p
												style={{
													fontSize: 11,
													fontWeight: 700,
													letterSpacing: 1,
													color: PRIMARY_TEXT,
													marginBottom: 8,
													textTransform: 'uppercase',
												}}
											>
												{t('avgUnpaidInstallmentAmount')}
											</p>
											<div
												style={{
													background: PRIMARY_SOFT,
													border: `1px solid ${PRIMARY_BORDER}`,
													borderRadius: 10,
													padding: '16px 20px',
													display: 'flex',
													alignItems: 'baseline',
													gap: 8,
												}}
											>
												<span
													style={{
														fontSize: 28,
														fontWeight: 800,
														lineHeight: 1,
														color: PRIMARY_TEXT,
													}}
												>
													{fmt(get(unpaidSummary, 'AvgOverdueAmount'))}
												</span>
												<span
													style={{
														fontSize: 14,
														color: PRIMARY_TEXT,
														fontWeight: 600,
													}}
												>
													USD
												</span>
											</div>
										</div>
									</>
								)}
							</div>
						</div>
					</>
				)}

				{/* Footer */}
				<div
					style={{
						display: 'flex',
						gap: 12,
						justifyContent: 'flex-end',
						paddingTop: 8,
						borderTop: '1px solid #f3f4f6',
					}}
				>
					<button
						onClick={handleDownload}
						style={{
							padding: '9px 22px',
							borderRadius: 8,
							background: PRIMARY_COLOR,
							color: '#111827',
							border: 'none',
							fontWeight: 600,
							cursor: 'pointer',
							fontSize: 14,
						}}
					>
						{t('downloadReport')}
					</button>
					<button
						onClick={() => setIsOpenModal(false)}
						style={{
							padding: '9px 22px',
							borderRadius: 8,
							background: '#fff',
							color: '#374151',
							border: '1px solid #d1d5db',
							fontWeight: 600,
							cursor: 'pointer',
							fontSize: 14,
						}}
					>
						{t('close')}
					</button>
				</div>
			</div>
		</Modal>
	)
}

export default memo(ClientPortfolioModal)
